import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { storeSession, getSession, clearSession as clearSessionStorage } from '@/utils/token-handler'
import { getUserProfile } from '@/api/auth'

export const useUserStore = defineStore('user', () => {
  // State
  const token = ref(null)
  const userInfo = ref(null)
  const permissions = ref([])
  const openid = ref(null)

  // Getters
  const isLoggedIn = computed(() => !!token.value)
  const userRole = computed(() => {
    // Handle both string role and role array from API
    const role = userInfo.value?.role
    if (Array.isArray(role)) {
      return role[0] || 'user'
    }
    return role || 'user'
  })
  const username = computed(() => userInfo.value?.username || userInfo.value?.name || '')
  const userId = computed(() => userInfo.value?.id || null)
  const userOpenid = computed(() => openid.value || userInfo.value?.openId || userInfo.value?.openid || null)

  // Actions
  const loadSession = () => {
    try {
      const session = getSession()
      if (session) {
        token.value = session.token
        userInfo.value = session.userInfo
        permissions.value = session.permissions || []
        console.log('✅ Session loaded from storage')
        return true
      }
      return false
    } catch (error) {
      console.error('❌ Failed to load session:', error)
      clearSession()
      return false
    }
  }

  const saveSession = () => {
    try {
      const data = {
        token: token.value,
        userInfo: userInfo.value,
        permissions: permissions.value,
        loginTime: Date.now()
      }
      const stored = storeSession(data)
      if (stored) {
        console.log('✅ Session saved')
      }
    } catch (error) {
      console.error('❌ Failed to save session:', error)
    }
  }

  const clearSession = () => {
    try {
      clearSessionStorage()
      token.value = null
      userInfo.value = null
      permissions.value = []
      console.log('✅ Session cleared')
    } catch (error) {
      console.error('Failed to clear session:', error)
    }
  }

  const setAuth = (tokenValue, userInfoValue, permissionsValue = []) => {
    token.value = tokenValue
    userInfo.value = userInfoValue
    permissions.value = permissionsValue || []
    openid.value = userInfoValue?.openId || userInfoValue?.openid || null
    saveSession()
  }

  const updateUserInfo = (data) => {
    if (userInfo.value) {
      userInfo.value = { ...userInfo.value, ...data }
      saveSession()
    }
  }

  const hasPermission = (permission) => {
    return permissions.value.includes(permission) || permissions.value.includes('admin') || permissions.value.includes('*')
  }

  const hasRole = (role) => {
    return userRole.value === role || userRole.value === 'admin'
  }

  const fetchUserProfile = async () => {
    try {
      console.log('📥 Fetching user profile...')
      const res = await getUserProfile()
      
      if (res.success && res.data) {
        console.log('✅ Profile fetched successfully')
        // Merge profile data with existing user info
        if (userInfo.value) {
          userInfo.value = { ...userInfo.value, ...res.data }
        } else {
          userInfo.value = res.data
        }
        saveSession()
        return res.data
      } else {
        console.warn('⚠️ Profile fetch returned no data')
        return null
      }
    } catch (error) {
      console.error('❌ Failed to fetch user profile:', error)
      return null
    }
  }

  // Defer session loading to router guard to avoid blocking initialization
  // loadSession() - called in router guard when needed

  return {
    token,
    userInfo,
    permissions,
    openid,
    isLoggedIn,
    userRole,
    username,
    userId,
    userOpenid,
    setAuth,
    clearSession,
    updateUserInfo,
    hasPermission,
    hasRole,
    loadSession,
    fetchUserProfile
  }
})